import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import AvailabilityService from '@/services/AvailabilityService'
import { buildDetailsTitle } from '../../common/availabilityUtils'

export default function useDyspozycjaDetails() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id } = useParams()

	const [availability, setAvailability] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [deleting, setDeleting] = useState(false)

	useEffect(() => {
		if (!id) {
			setError(t('trainingAvailability.details.messages.missingReference'))
			setLoading(false)
			return
		}

		let cancelled = false

		const load = async () => {
			setLoading(true)
			setError('')

			const result = await AvailabilityService.getById(id)
			if (cancelled) return

			if (result.error) {
				setAvailability(null)
				setError(result.error)
			} else {
				setAvailability(result.data)
			}
			setLoading(false)
		}

		load()
		return () => {
			cancelled = true
		}
	}, [id, t])

	const slots = availability?.slots ?? []
	const title = useMemo(() => (availability ? buildDetailsTitle(availability, t) : ''), [availability, t])

	const handleBack = useCallback(() => {
		navigate('/centrumSzkolen/dyspozycja')
	}, [navigate])

	const handleEdit = useCallback(() => {
		if (!id) return
		navigate(`/centrumSzkolen/dyspozycja/edit/${id}`)
	}, [id, navigate])

	const handleDelete = useCallback(async () => {
		if (!id || deleting) return

		const confirm = await Swal.fire({
			title: t('notifications.confirmTitle'),
			text: t('trainingAvailability.details.deleteConfirm'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			confirmButtonText: t('notifications.confirmDeleteButton'),
			cancelButtonText: t('notifications.cancelButton'),
		})
		if (!confirm.isConfirmed) return

		setDeleting(true)
		const result = await AvailabilityService.requestChange(id, 'delete')

		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('trainingAvailability.details.messages.requestFailed'),
				'error'
			)
			setDeleting(false)
			return
		}

		await Swal.fire(
			t('notifications.successTitle'),
			t('trainingAvailability.details.messages.requestSent'),
			'success'
		)
		navigate('/centrumSzkolen/dyspozycja')
	}, [deleting, id, navigate, t])

	return {
		availability,
		deleting,
		error,
		handleBack,
		handleDelete,
		handleEdit,
		loading,
		slots,
		title,
	}
}
